import { Accordion } from "@mui/material";
import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Permissions,
	translate,
	hasCompanyPermission,
	hasPermission,
	isSegFeatureEnabled,
	SegFlags,
} from "../../../../../../../../../common/providers";
import {
	CircularLoader,
	IconComponent,
	icon,
	VersionComparisonPanel,
} from "../../../../../../../../../common/components";
import { ApiService, ContentService, DocumentService, VersionRedressmentService } from "../../../../../../../../../api";
import styles from "./VersioningTabView.module.css";
import { setDocumentInfo, setSelectedSearch } from "../../../../../../../slice/document/document-slice";
import { setHasVersioningUpdate, updateNode } from "../../../../../../../slice/pdf/pdf-slice";
import { useApi, useTrackEvent } from "../../../../../../../../../common/hooks";
import AccordionUploaded from "./versioning-tab-accordion/AccordionUploaded";
// import AccordionOriginal from "./versioning-tab-accordion/AccordionOriginal";

const convertStatus = (status) => {
	let newStatus = status.replace("UPDATED", "MODIFIED");
	if (newStatus.includes("MODIFIED") && !newStatus.includes("MINOR")) {
		newStatus = "MODIFIED";
	}
	return newStatus;
};
export default function VersioningTabView({ informationId, pageStart, selectedMode }) {
	const { call, cancel } = useApi(DocumentService.getVersions);
	const [original, setOriginal] = useState(null);
	// eslint-disable-next-line no-unused-vars
	const [previousUpload, setPreviousUpload] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [displayError, setDisplayError] = useState(false);
	const [coordinates, setCoordinates] = useState([]);
	const [previousCoordinates, setPreviousCoordinates] = useState([]);
	const [comparisonOpen, setComparisonOpen] = useState(false);
	// const [originalContent, setOriginalContent] = useState("");
	const [content, setContent] = useState("");
	const [idValue, setIdValue] = useState(null);
	const [status, setStatus] = useState("");
	const [infoUpdated, setInfoUpdated] = useState(false);
	const docId = useSelector(({ srDocument }) => srDocument.documentId);
	const versionStatuses = useSelector(({ srDocument }) => srDocument.versionStatuses);
	const previousVersionId = useSelector(({ srDocument }) => srDocument.previousVersionId);
	const previousVersionName = useSelector(({ srDocument }) => srDocument.previousVersionName);
	const documentInfo = useSelector(({ srDocument }) => srDocument);
	const currentPageDimensions = useSelector(({ srDocument }) => srDocument.pageDimensions);
	const currentNumberOfPages = useSelector(({ srDocument }) => srDocument.numberOfPages);
	const currentContent = useSelector(({ srPdf }) => srPdf.content);
	const openComparisonFromRedux = useSelector(({ srDocument }) => srDocument.selectedSearch?.openComparison);
	const dispatch = useDispatch();
	const cancelTokenSourceRef = useRef(null);
	const trackEvent = useTrackEvent();
	const fetchPreviousDetails = useCallback(() => {
		if (previousVersionId || previousVersionName) {
			VersionRedressmentService.getPreviousInformationDetails(
				{ informationId },
				cancelTokenSourceRef.current.token
			)
				.then((data) => {
					setOriginal(data);
				})
				.catch((err) => {
					console.error(err);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
		setIsLoading(false);
	}, [informationId, previousVersionId, previousVersionName]);
	useEffect(() => {
		if (cancelTokenSourceRef.current) {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		}
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		if (isSegFeatureEnabled(SegFlags.VERSIONS)) {
			setIsLoading(true);
			if (informationId) {
				fetchPreviousDetails();
			}
		}
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, [informationId, fetchPreviousDetails]);
	useEffect(() => {
		if (!documentInfo) {
			DocumentService.getInformation({ docId }, cancelTokenSourceRef.current.token)
				.then((docInfo) => dispatch(setDocumentInfo(docInfo)))
				.catch((error) => console.error(error));
		}
	}, [docId, documentInfo, dispatch]);
	useEffect(() => {
		if (original?.previousInfoId) {
			setIdValue(original?.previousInfoId);
		} else {
			setIdValue(0);
		}
		setStatus(original?.status || "NEW");
		if (original?.previousContent) {
			setContent(original?.content);
			// setOriginalContent(original?.previousContent);
		}
	}, [original]);
	useEffect(() => {
		if (isSegFeatureEnabled(SegFlags.VERSIONS)) {
			call({ docId })
				.then((versions) => {
					setPreviousUpload(versions[1]?.uploaded);
				})
				.catch(console.error);
		}
		return () => {
			cancel();
		};
	}, [docId, call, cancel]);
	const handleApply = () => {
		const payload = { idv2: informationId, status };
		if (status !== "NEW") {
			payload.idv1 = Number.parseInt(idValue, 10);
		}
		VersionRedressmentService.updateInformationVersion(payload, cancelTokenSourceRef.current.token)
			.then(() => {
				dispatch(
					updateNode({
						mode: selectedMode,
						pageConditions: { page: pageStart },
						informationId,
						updates: [{ property: "infoVersionStatus", value: convertStatus(status) }],
					})
				);
				dispatch(setHasVersioningUpdate(true));
				fetchPreviousDetails(informationId);
				trackEvent("MyReview_V1V2_Update_previous_information_ID");
				if (infoUpdated) {
					trackEvent("MyReview_V1V2_Update_type");
				}
			})
			.catch((err) => console.error(err));
		setInfoUpdated(false);
	};
	const handleChangeInformationId = (e) => {
		const { value } = e.target;
		if (value > 0) {
			setDisplayError(false);
			setIdValue(value);
			if (value === original?.previousInfoId.toString()) {
				setInfoUpdated(false);
			} else {
				setInfoUpdated(true);
			}
		}
	};

	const handleOpenComparison = () => {
		setComparisonOpen(true);
		// Fetch coordinates for current version
		ContentService.getContentCoordinatesFromInformation(
			{
				docId,
				informationId,
			},
			cancelTokenSourceRef.current.token
		)
			.then((data) => {
				setCoordinates(data);
			})
			.catch((err) => console.error(err));
		// Fetch coordinates for previous version
		if (idValue && previousVersionId) {
			ContentService.getContentCoordinatesFromInformation(
				{
					docId: previousVersionId,
					informationId: idValue,
				},
				cancelTokenSourceRef.current.token
			)
				.then((data) => {
					setPreviousCoordinates(data);
				})
				.catch((err) => console.error(err));
		}
	};
	const handleCloseComparison = () => {
		setComparisonOpen(false);
		setCoordinates([]);
		setPreviousCoordinates([]);
	};

	// Auto-open comparison panel when triggered from RequirementVersionOverlay
	// We use a ref to track the pending open state
	const pendingOpenComparisonRef = useRef(false);

	useEffect(() => {
		if (openComparisonFromRedux) {
			pendingOpenComparisonRef.current = true;
			// Clear the flag immediately to prevent re-triggering on other re-renders
			dispatch(setSelectedSearch({ openComparison: false }));
		}
	}, [openComparisonFromRedux, dispatch]);

	useEffect(() => {
		if (pendingOpenComparisonRef.current && !isLoading && original) {
			pendingOpenComparisonRef.current = false;
			handleOpenComparison();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, original]);

	const handleSelect = (e) => {
		const { value } = e.target;
		setStatus(value);
		if (value === original?.status) {
			setInfoUpdated(false);
		} else {
			setInfoUpdated(true);
		}
	};
	const hasLeaderPermissions =
		hasCompanyPermission(Permissions.PROJECT_LEADER) && hasPermission(Permissions.PROJECT_LEADER);
	return (
		<div className={styles.container}>
			{isSegFeatureEnabled(SegFlags.VERSIONS) ? (
				(documentInfo?.previousVersionId && original && !isLoading && (
					<div className={styles.versioningTabView}>
						<Accordion>
							<AccordionUploaded
								content={content}
								dispatch={dispatch}
								displayError={displayError}
								documentInfo={documentInfo}
								handleApply={handleApply}
								handleChange={handleChangeInformationId}
								handleOpenComparison={handleOpenComparison}
								handleSelect={handleSelect}
								hasLeaderPermissions={hasLeaderPermissions}
								idValue={idValue}
								informationId={informationId}
								infoUpdated={infoUpdated}
								original={original}
								status={status}
								versionStatuses={versionStatuses}
							/>
						</Accordion>
						{/* Hidden since it is complicated to display OG version. But we might do it later on */}
						{/* {original.previousContent && (
							<>
								<Accordion>
									<AccordionSummary
										className={styles.accordionSummary}
										expandIcon={<IconComponent icon={icon.faChevronDown} />}
									>
										<div className={styles.subtitle}>
											{documentInfo?.uploaded &&
												translate(
													"smart-review.cmp-details.option-panel.versioning.original-date",
													{
														informationDate: translateDate(previousUpload),
													}
												)}
										</div>
									</AccordionSummary>
									<AccordionOriginal
										documentInfo={documentInfo}
										handleClickDisplay={handleClickDisplay}
										original={original}
										originalContent={originalContent}
										previousUpload={previousUpload}
										previousVersion={previousVersion}
									/>
								</Accordion>
							</>
						)} */}
					</div>
				)) ||
				(!isLoading && (
					<div className={styles.emptyState}>
						{translate("smart-review.cmp-details.option-panel.versioning.no-previous-version")}
					</div>
				))
			) : (
				<div className={styles.noContent}>
					<IconComponent
						className={styles.noContent__infoIcon}
						color="var(--blue-500)"
						icon={icon.faInfoCircle}
						size="sm"
					/>
					<div className={styles.noContent__desc}>
						{translate("smart-review.cmp-details.option-panel.versioning.no-content-version")}
					</div>
				</div>
			)}
			{isLoading && (
				<div className={styles.loading}>
					<CircularLoader />
				</div>
			)}
			<VersionComparisonPanel
				enableLeftApiCall
				coordinates={coordinates}
				currentContent={currentContent}
				currentDocId={docId}
				currentDocName={documentInfo?.documentName}
				currentNumberOfPages={currentNumberOfPages}
				currentPage={pageStart || 1}
				currentPageDimensions={currentPageDimensions}
				enableRightApiCall={false}
				initialLeftInfoId={informationId}
				initialLeftPage={pageStart || 1}
				initialRightInfoId={original?.previousInfoId}
				initialRightPage={original?.previousPage || 1}
				open={comparisonOpen}
				previousCoordinates={previousCoordinates}
				previousDocId={previousVersionId}
				previousDocName={previousVersionName || documentInfo?.previousVersionName}
				previousPage={original?.previousPage || 1}
				projectId={documentInfo?.projectId}
				onClose={handleCloseComparison}
			/>
		</div>
	);
}
